import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import ActionTokenService from '../../services/actionTokenService';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useTranslation } from 'react-i18next';
import './ActionTokenHandler.css';

export const ActionTokenHandler = () => {
    const location = useLocation();
    const history = useHistory();
    const { t } = useTranslation();
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [errorType, setErrorType] = useState('generic'); // generic, expired
    const [countdown, setCountdown] = useState(5);
    const actionTokenService = new ActionTokenService();

    const isDeactivationRoute = location.pathname === '/desactivation-recherche-active';

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const token = searchParams.get('token');
        const id = searchParams.get('id');

        if (token && id) {
            actionTokenService.handleToken(id, token)
                .then(() => {
                    setStatus('success');
                })
                .catch((error) => {
                    if (isDeactivationRoute) {
                        let errorMessage = error.response?.data?.message || error.message || '';
                        // Cleaning up the "Http Error " prefix if present
                        if (errorMessage.startsWith("Http Error ")) {
                            errorMessage = errorMessage.replace("Http Error ", "");
                        }
                        
                        const msgLower = errorMessage.toLowerCase();
                        // Normalization to handle accents robustly
                        const msgNormalized = msgLower.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

                        if (msgLower.includes('déjà utilisé') || msgLower.includes('déjà été utilisé') || msgNormalized.includes('deja utilise') || msgNormalized.includes('deja ete utilise') || msgLower.includes('already used')) {
                            setStatus('success');
                        } else if (msgLower.includes('expiré') || msgNormalized.includes('expire') || msgLower.includes('expired')) {
                            setErrorType('expired');
                            setStatus('error');
                        } else {
                            setErrorType('generic');
                            setStatus('error');
                        }
                    } else {
                        setErrorType('generic');
                        setStatus('error');
                    }
                });
        } else {
            setStatus('error');
            setErrorType('generic');
        }
    }, [location, isDeactivationRoute]);

    useEffect(() => {
        let timer;
        if (status === 'success' && countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        } else if (status === 'success' && countdown === 0) {
            history.push('/');
        }
        return () => clearInterval(timer);
    }, [status, countdown, history]);

    const handleGoHome = () => {
        history.push('/');
    };

    const handleContactSupport = () => {
        window.location.href = "mailto:support@diplomadvisor.com";
    };

    const handleLogin = () => {
        history.push('/login?User=Student');
    };

    return (
        <div className="action-token-container">
            {status === 'loading' && (
                <div className="action-token-card">
                    <ProgressSpinner style={{width: '50px', height: '50px'}} strokeWidth="4" animationDuration=".5s"/>
                    <p className="p-mt-3">{t('actionToken.loading')}</p>
                </div>
            )}

            {status === 'success' && (
                <div className="action-token-card">
                    <div className="icon-container success">
                        <i className="pi pi-check"></i>
                    </div>
                    <h2 className="action-title">{t('actionToken.success_title')}</h2>
                    <p className="action-message">
                        {t('actionToken.success_message')}
                    </p>
                    <p className="countdown-text">
                        {t('actionToken.redirection')} <span className="countdown-number">{countdown}</span> {t('actionToken.seconds')}
                    </p>
                </div>
            )}

            {status === 'error' && (
                <div className="action-token-card">
                    <div className="icon-container error">
                        <i className="pi pi-exclamation-circle"></i>
                    </div>
                    
                    {errorType === 'expired' ? (
                        <>
                            <h2 className="action-title">{t('actionToken.expired_title')}</h2>
                            <p className="action-message">
                                {t('actionToken.expired_message')}
                            </p>
                            <div className="action-buttons">
                                <button className="btn btn-primary" onClick={handleGoHome}>
                                    <i className="pi pi-home"></i> {t('actionToken.return_home')}
                                </button>
                                <button className="btn btn-secondary" onClick={handleLogin}>
                                    <i className="pi pi-sign-in"></i> {t('login.log_in')}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <h2 className="action-title">{t('actionToken.error_title')}</h2>
                            <p className="action-message">
                                {t('actionToken.error_message')}
                            </p>
                            <div className="action-buttons">
                                <button className="btn btn-primary" onClick={handleGoHome}>
                                    <i className="pi pi-home"></i> {t('actionToken.return_home')}
                                </button>
                                <button className="btn btn-secondary" onClick={handleContactSupport}>
                                    <i className="pi pi-envelope"></i> {t('actionToken.contact_support')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
